import React from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, Sparkles, Mars, Venus, Transgender } from "lucide-react";
import { useHOH, STEP } from "../../../context/StyleContext";
import StepFrame, { StepTitle } from "../StepFrame";

const DIRECTIONS = [
  { id: "masculine", label: "Masculine", icon: Mars },
  { id: "feminine", label: "Feminine", icon: Venus },
  { id: "androgynous", label: "Androgynous", icon: Transgender },
];

export default function HairstylePreviewStep() {
  const { needsDirection, chooseDirection, hairPreviews, loadingHair, selectedHair, setSelectedHair, loadOutfits, goTo } = useHOH();

  if (needsDirection) {
    return (
      <StepFrame className="text-center">
        <StepTitle overline="Step 3" title="Choose your styling direction" subtitle="We couldn’t confidently detect a direction — pick the styling you prefer." />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
          {DIRECTIONS.map((d) => {
            const Icon = d.icon;
            return (
              <button key={d.id} onClick={() => chooseDirection(d.id)} className="hoh-card p-8 flex flex-col items-center gap-3 hover:-translate-y-1 transition-transform" data-testid={`direction-${d.id}`}>
                <Icon className="w-8 h-8 text-hoh-rose" />
                <span className="font-semibold text-black uppercase tracking-wider text-sm">{d.label}</span>
              </button>
            );
          })}
        </div>
      </StepFrame>
    );
  }

  const proceed = () => { loadOutfits(); goTo(STEP.OUTFIT); };

  return (
    <StepFrame wide className="text-center">
      <StepTitle overline="Step 3" title="Your hairstyle previews" subtitle="Generated on your own photo — your face stays exactly the same." />

      {loadingHair || !hairPreviews ? (
        <div className="grid md:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="hoh-card p-6">
              <div className="aspect-[3/4] rounded-[18px] bg-hoh-lavender flex flex-col items-center justify-center">
                <Loader2 className="w-8 h-8 text-hoh-rose animate-spin" />
                <p className="mt-3 text-xs text-hoh-slate">Styling your hair…</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-3 gap-6">
            {hairPreviews.map((h, i) => {
              const selected = selectedHair?.name === h.name;
              return (
                <motion.div key={h.tier + i} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  className={`hoh-card p-4 flex flex-col ${selected ? "hoh-glow-border" : ""}`} data-testid={`hair-preview-${i}`}>
                  <div className="relative aspect-[3/4] rounded-[18px] overflow-hidden">
                    <img src={h.image} alt={h.name} className="w-full h-full object-cover" data-testid={`hair-preview-image-${i}`} />
                    <span className="absolute top-3 left-3 glass rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-hoh-navy">{h.tier}</span>
                    <span className="absolute top-3 right-3 glass rounded-full px-2.5 py-0.5 text-xs font-semibold text-hoh-navy flex items-center gap-1"><Sparkles className="w-3 h-3 text-hoh-rose" />{h.confidence}%</span>
                  </div>
                  <h3 className="mt-4 text-xl font-semibold text-black">{h.name}</h3>
                  <p className="mt-1 text-sm text-hoh-slate flex-1">{h.reason}</p>
                  <button onClick={() => setSelectedHair(h)} className={`mt-4 rounded-full px-6 py-3 font-semibold uppercase tracking-wider text-xs transition-colors ${selected ? "bg-hoh-rose text-hoh-navy" : "bg-hoh-navy text-white hover:bg-[#2D3A54]"}`} data-testid={`hair-select-${i}`}>
                    {selected ? <span className="flex items-center justify-center gap-1.5"><Check className="w-4 h-4" /> Selected</span> : "Select"}
                  </button>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-10 min-h-[60px]">
            {selectedHair && (
              <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onClick={proceed} className="btn-royal" data-testid="hair-continue-btn">
                Continue To Outfits <ArrowRight className="w-4 h-4" />
              </motion.button>
            )}
          </div>
        </>
      )}
    </StepFrame>
  );
}
